/* Failures — why placements fail, how much of it we recover, and what it costs.

   Two things worth knowing when reading this tab:

   1. `recovered` counts failed placements whose reference_number later produced a
      successful row. Those cost latency and an ops nudge, not the order. The number
      that actually matters is `unrecovered`.
   2. Reasons are families, not raw courier text — courier errors embed AWBs and
      pincodes, so grouping the raw string yields thousands of one-off rows. The
      classifier lives server-side (PlacementFailureClassifier); each family carries
      up to three verbatim examples so nothing is hidden behind the label. */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import {LoadErrorPanel, TableSkeleton} from '../../components'
import {
  usePcisHealthBreakdownQuery,
  usePcisHealthFailuresQuery,
  usePcisHealthOrdersQuery,
} from '../../../../redux/supplyApiSlice'
import {
  AMBER,
  CORAL,
  Empty,
  INDIGO,
  LabelledBar,
  Meter,
  MiniTable,
  Panel,
  Pill,
  SLATE,
  Stat,
  StatGrid,
  TEAL,
  dedupeLatestByRef,
  money,
  nf,
  pct,
  rateBand,
} from './parts'

/* Links inside .supply-scope need inline colour: the scope's stylesheet has
   `.supply-scope a {color: inherit}` at specificity (0,1,1), which beats emotion's
   sx-generated class (0,1,0). Same lesson as PcisHealthDetails. */
const linkStyle = {color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline', fontSize: 12}

const fmtTime = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d) ? v : d.toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'})
}

export default function TabFailures({filters, pollMs, onOpenOrders}) {
  const poll = pollMs ? {pollingInterval: pollMs} : undefined

  const {data: fail, isFetching, isError, error, refetch} = usePcisHealthFailuresQuery(filters, poll)
  const {data: byCourier} = usePcisHealthBreakdownQuery({...filters, dimension: 'COURIER', limit: 100}, poll)
  /* The feed is the same /health/orders the Details page uses, pinned to failures
     and the first page — a sample, not a browsable list. "Open in Order Explorer"
     hands the full filter set to the tab that does pagination properly. */
  const {data: recent} = usePcisHealthOrdersQuery({...filters, onlyFailed: true, page: 0, size: 8}, poll)

  const worstCouriers = useMemo(
    () =>
      (byCourier?.rows || [])
        .filter((r) => r.failed > 0)
        .sort((a, b) => b.failureRatePct - a.failureRatePct),
    [byCourier]
  )
  const worstMax = worstCouriers[0]?.failureRatePct || 1
  const reasonMax = fail?.reasons?.[0]?.count || 1
  const recentRows = useMemo(() => dedupeLatestByRef(recent?.orders || []), [recent])

  if (isError) return <LoadErrorPanel message={error?.message || 'Could not load failure data.'} onRetry={refetch} />
  if (isFetching && !fail) return <TableSkeleton kpis={4} rows={6} />
  if (!fail) return <Empty />

  if (!fail.failedOrders) {
    return (
      <>
        <StatGrid>
          <Stat label='Failed placements' value='0' palette={TEAL} sub={`of ${nf(fail.totalOrders)} orders`} />
          <Stat label='Failure rate' value='0.00' unit='%' palette={TEAL} sub='Clean window' />
        </StatGrid>
        <Panel sx={{mt: 2}}>
          <Empty>No placement failed in this window. Nothing to explain.</Empty>
        </Panel>
      </>
    )
  }

  return (
    <>
      <StatGrid>
        <Stat
          label='Failed placements'
          value={nf(fail.failedOrders)}
          palette={CORAL}
          share={fail.failureRatePct}
          sub={`of ${nf(fail.totalOrders)} orders`}
        />
        <Stat
          label='Failure rate'
          value={Number(fail.failureRatePct).toFixed(2)}
          unit='%'
          palette={rateBand(fail.failureRatePct).c}
          sub={rateBand(fail.failureRatePct).label}
        />
        <Stat
          label='Recovered on retry'
          value={nf(fail.recovered)}
          palette={AMBER}
          share={fail.recoveryRatePct}
          sub={`${pct(fail.recoveryRatePct)} eventually placed`}
        />
        <Stat
          label='Never placed'
          value={nf(fail.unrecovered)}
          palette={CORAL}
          sub={`${money(fail.failedInvoiceValue)} invoice value at stake`}
        />
      </StatGrid>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1.25fr) minmax(0, 1fr)'}, gap: 2, mt: 2}}>
        <Panel title='Why placements fail' note='Grouped into families — hover a row for real courier text'>
          <Stack spacing={1.5}>
            {fail.reasons.map((r, i) => (
              <Tooltip
                key={r.reason}
                title={
                  r.examples?.length ? (
                    <Box sx={{fontSize: 11.5, lineHeight: 1.6}}>
                      {r.examples.map((ex, j) => (
                        <Box key={j} sx={{mb: j === r.examples.length - 1 ? 0 : 0.75}}>
                          “{ex}”
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    ''
                  )
                }
                placement='top-start'
              >
                <Box>
                  <Stack direction='row' justifyContent='space-between' sx={{mb: 0.5}}>
                    <Typography sx={{fontSize: 12.5, fontWeight: 600}}>{r.reason}</Typography>
                    <Typography sx={{fontSize: 12.5}}>
                      <Box component='span' sx={{fontWeight: 700}}>{nf(r.count)}</Box>
                      <Box component='span' sx={{color: 'text.secondary', ml: 0.75}}>{pct(r.sharePct)}</Box>
                    </Typography>
                  </Stack>
                  <Meter
                    value={(r.count / reasonMax) * 100}
                    palette={i === 0 ? CORAL : i < 3 ? AMBER : SLATE}
                    height={8}
                  />
                </Box>
              </Tooltip>
            ))}
          </Stack>
        </Panel>

        <Panel title='Failure rate by courier' note='Worst first · only couriers with at least one failure'>
          <Stack spacing={1.25}>
            {worstCouriers.map((r) => (
              <LabelledBar
                key={r.key}
                label={r.key}
                sublabel={`${nf(r.failed)} of ${nf(r.total)}`}
                value={pct(r.failureRatePct)}
                share={(r.failureRatePct / worstMax) * 100}
                palette={rateBand(r.failureRatePct).c}
              />
            ))}
          </Stack>
        </Panel>
      </Box>

      <Panel
        title='Recent failures'
        note='Newest 8 in this window, one row per reference number — a sample, not the full list'
        sx={{mt: 2}}
        action={
          onOpenOrders ? (
            <Box component='button' type='button' onClick={onOpenOrders} sx={{border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', ...linkStyle}}>
              Open in Order Explorer →
            </Box>
          ) : null
        }
      >
        <MiniTable
          columns={[
            {key: 'createdAt', header: 'Time', render: (r) => <Typography sx={{fontSize: 12}}>{fmtTime(r.createdAt)}</Typography>},
            {
              key: 'courier',
              header: 'Courier / account',
              render: (r) => (
                <Box>
                  <Typography sx={{fontSize: 12.5, fontWeight: 700, lineHeight: 1.25}}>{r.courierCode}</Typography>
                  <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2, wordBreak: 'break-all'}}>
                    {r.accountCode || '—'}
                  </Typography>
                </Box>
              ),
            },
            {
              key: 'referenceNumber',
              header: 'Reference',
              render: (r) => <Typography sx={{fontSize: 11.5, fontFamily: 'monospace', wordBreak: 'break-all'}}>{r.referenceNumber}</Typography>,
            },
            {
              key: 'failureReason',
              header: 'Reason',
              render: (r) => (
                <Tooltip title={r.failureReason || ''}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: CORAL.main,
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      maxWidth: 320,
                    }}
                  >
                    {r.failureReason || '—'}
                  </Typography>
                </Tooltip>
              ),
            },
            {
              key: 'route',
              header: 'Route',
              render: (r) => (
                <Typography sx={{fontSize: 12}}>
                  {r.pickupPincode || '—'} <Box component='span' sx={{color: 'text.disabled'}}>→</Box> {r.dropPincode || '—'}
                </Typography>
              ),
            },
            {
              key: 'type',
              header: 'Type',
              render: (r) => <Pill palette={r.orderType === 'COD' ? AMBER : INDIGO}>{r.orderType || '—'}</Pill>,
            },
            {key: 'invoiceValue', header: 'Value', align: 'right', render: (r) => <Typography sx={{fontSize: 12, fontWeight: 700}}>{money(r.invoiceValue)}</Typography>},
          ]}
          rows={recentRows}
          getKey={(r) => r.id}
          empty='No failed orders on this page.'
        />
      </Panel>
    </>
  )
}

TabFailures.propTypes = {filters: PropTypes.object.isRequired, pollMs: PropTypes.number, onOpenOrders: PropTypes.func}
